import type { RefObject } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { LAYOUT, SEARCH_PAGE_Z_INDEX } from '@rover/constants/src/search/searchPage.constants';
import { TABLET_MAX, TABLET_MIN } from '@rover/kibble/styles/MediaQuery';
import RoverLoading from '@rover/react-lib/src/components/utils/RoverLoading';
import SearchTrustSafetyBanner from '@rover/react-lib/src/pages/search/SearchPage/components/SearchResults/SearchTrustSafetyBanner';
import type { SearchResultsObservation } from '@rover/react-lib/src/pages/search/SearchPage/SearchPage.types';

import { useSimplifiedDesktopFilters } from '../../utilities/useSimplifiedDesktopFilters';

import SearchResultsContainer from './SearchResultsContainer';

export type Props = {
  isMapExpanded: boolean;
  isSearchLoading: boolean;
  onObserve: (observation: SearchResultsObservation) => void;
  scrollToResults: boolean;
  searchCardRefs?: (instance: HTMLDivElement, index: number) => void;
  resultsRef: RefObject<any>;
  isSeoPage: boolean;
  userInEurope: boolean;
  showWizardFilters: boolean;
  isRollout1: boolean;
  inVariantSimplifiedMobileOwnerSearchFormExperiment: boolean;
};
const StyledRoverLoading = styled(RoverLoading)<{
  $isSimplifiedFilters?: boolean;
}>`
  flex: 1;
  background-color: ${({ $isSimplifiedFilters, theme }) =>
    $isSimplifiedFilters ? theme.background.primary : theme.background.secondary};

  &:after {
    top: 40px !important;
  }
`;

const DESKTOP = `@media(min-width:${TABLET_MAX + 1}px)`;
const TABLET = `@media (min-width: ${TABLET_MIN}px and max-width: ${TABLET_MAX}px)`;

const Results = styled.div<{
  isMapExpanded: boolean;
  isSimplifiedFilters?: boolean;
}>`
  flex: 1;
  background-color: ${({ theme }) => theme.background.primary};
  align-self: stretch;
  will-change: transform;
  z-index: ${SEARCH_PAGE_Z_INDEX.SEARCH_RESULTS_WRAPPER};

  &:focus-within {
    z-index: ${SEARCH_PAGE_Z_INDEX.STICKY_SIDEBAR};
  }

  ${DESKTOP} {
    border-radius: ${({ theme }) => `${theme.radii.secondary} 0 0 ${theme.radii.secondary}`};
    flex: 0 0;
    width: 0;
  }

  ${({ isMapExpanded, isSimplifiedFilters = false }) => {
    // For top-filters layout, use flex: 1 (fill remaining space)
    if (isSimplifiedFilters) {
      return css`
        ${DESKTOP} {
          flex: 1 1 auto;
          width: auto;
          min-width: 0;
        }
      `;
    }
    // For sidebar layout, use dynamic calculation
    const WIDTH_PERCENT = isMapExpanded ? LAYOUT.NARROW_WIDTH_PERCENT : LAYOUT.WIDE_WIDTH_PERCENT;
    return css`
      ${TABLET} {
        flex-basis: calc(${LAYOUT.AVAILABLE_WIDTH} * ${LAYOUT.WIDE_WIDTH_PERCENT});
      }
      ${DESKTOP} {
        flex-basis: calc(${LAYOUT.AVAILABLE_WIDTH} * ${WIDTH_PERCENT});
      }
    `;
  }};
`;
type ObserverProps = {
  bound: 'top' | 'bottom';
  rootMargin: string;
};

export const SearchResultsWrapper: React.FC<Props> = ({
  isMapExpanded,
  isSearchLoading,
  onObserve,
  scrollToResults,
  searchCardRefs,
  resultsRef,
  isSeoPage,
  userInEurope,
  showWizardFilters,
  isRollout1,
  inVariantSimplifiedMobileOwnerSearchFormExperiment,
}) => {
  const [viewportIsBelowTop, setViewportIsBelowTop] = useState(false);
  const [viewportIsAboveBottom, setViewportIsAboveBottom] = useState(false);
  const { isActive: isSimplifiedFilters } = useSimplifiedDesktopFilters();

  const observersRef = useRef<{
    top: IntersectionObserver;
    bottom: IntersectionObserver;
  } | null>(null);

  const onObserveIntersection = useCallback(() => {
    if (onObserve) {
      onObserve({
        viewportIsBelowTop,
        viewportIsAboveBottom,
      });
    }
  }, [onObserve, viewportIsBelowTop, viewportIsAboveBottom]);

  const createObserver = useCallback(
    ({ bound, rootMargin }: ObserverProps): IntersectionObserver => {
      const onThresholdCross = (entries: IntersectionObserverEntry[]): void => {
        entries.forEach((entry) => {
          if (bound === 'top') {
            setViewportIsBelowTop(entry.isIntersecting);
          }

          if (bound === 'bottom') {
            setViewportIsAboveBottom(entry.isIntersecting);
          }
        });
      };

      return new IntersectionObserver(onThresholdCross, {
        rootMargin,
      });
    },
    []
  );

  // Initialize observers on mount
  useEffect(() => {
    const areaAboveResults = '10000% 0px -100% 0px';
    const areaBelowResults = '-100% 0px 10000% 0px';
    observersRef.current = {
      top: createObserver({
        bound: 'top',
        rootMargin: areaAboveResults,
      }),
      bottom: createObserver({
        bound: 'bottom',
        rootMargin: areaBelowResults,
      }),
    };
    if (resultsRef.current) {
      observersRef.current.top.observe(resultsRef.current);
      observersRef.current.bottom.observe(resultsRef.current);
    }

    return () => {
      if (observersRef.current) {
        observersRef.current.top.disconnect();
        observersRef.current.bottom.disconnect();
      }
    };
  }, []);

  // Handle scroll to results on search loading
  useEffect(() => {
    if (isSearchLoading && scrollToResults) {
      if (resultsRef && resultsRef.current) {
        resultsRef.current.scrollIntoView();
      }
    }
  }, [isSearchLoading, scrollToResults, resultsRef]);

  // Trigger onObserve when viewport states change
  useEffect(() => {
    onObserveIntersection();
  }, [onObserveIntersection]);

  return (
    <Results
      ref={resultsRef}
      data-qa-id="search-results-wrapper"
      data-testid="search-results-wrapper"
      isMapExpanded={isMapExpanded}
      isSimplifiedFilters={isSimplifiedFilters}
    >
      <StyledRoverLoading isLoading={isSearchLoading} $isSimplifiedFilters={isSimplifiedFilters}>
        {isRollout1 && inVariantSimplifiedMobileOwnerSearchFormExperiment && showWizardFilters && (
          <SearchTrustSafetyBanner userInEurope={userInEurope} />
        )}
        <SearchResultsContainer searchCardRefs={searchCardRefs} isSeoPage={isSeoPage} />
      </StyledRoverLoading>
    </Results>
  );
};

export default SearchResultsWrapper;
